import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Search, eur, num, fdate, inputCls, btnGold, btnGhost } from './ui';

const FILTERS = {
  todos: 'Todos', sin_broker: 'Sin broker asignado', reservas: 'Reservas', no_pagado: 'Sin el 100 % pagado', sin_tc: 'Sin T&C firmados',
  anual: 'Anuales', vitalicia: 'Vitalicias',
};
const isLifetime = (m) => /vitalicia/i.test(m.membership_type || '');
const MATCH = {
  todos: () => true,
  sin_broker: (m) => m.missing_broker,
  reservas: (m) => m.reserve,
  no_pagado: (m) => !m.fully_paid && !m.reserve,
  sin_tc: (m) => !m.tc_signed_at,
  anual: (m) => !isLifetime(m),
  vitalicia: (m) => isLifetime(m),
};
// Renovación prevista de una anual: la caducidad del CDM si existe; si no, un año después del alta
const renewalOf = (m) => {
  if (m.expiry_date) return m.expiry_date;
  if (!m.join_date) return null;
  const d = new Date(`${m.join_date.slice(0, 10)}T12:00:00`);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};
export function ModeBadge({ m }) {
  if (isLifetime(m)) {
    return <Badge tone="good" title={m.membership_type}>{m.membership_type === 'Anual -> Vitalicia' ? 'Vitalicia (antes anual)' : 'Vitalicia'}</Badge>;
  }
  const r = renewalOf(m);
  return <Badge tone="info" title={m.expiry_date ? 'Caducidad registrada en el CDM' : 'Calculada: un año desde el alta'}>Anual{r ? ` · renueva ${fdate(r)}` : ''}</Badge>;
}

const REQUIRED = { status: 'el estado', sku: 'la membresía', membership_type: 'la modalidad', join_date: 'la fecha de alta' };

function MemberEditor({ m, cdm, onClose, onSaved }) {
  const lists = cdm?.lists || {};
  const presets = cdm?.rules?.rate_presets || [];
  const [form, setForm] = useState({
    sponsor_id: m.sponsor_id || '', status: m.status || '', rate_override: m.rate_override || '',
    sku: m.sku || '', membership_type: m.membership_type || '', join_date: m.join_date || '', notes: m.notes || '',
    channel: m.channel || '', email: m.email || '', phone: m.phone || '',
  });
  const [busy, setBusy] = useState(false);
  const missing = Object.entries(REQUIRED).filter(([k]) => !String(form[k] || '').trim()).map(([, v]) => v);
  const sponsorMissing = m.sponsor_id && !form.sponsor_id.trim();
  const accessLifetime = form.sku === 'Access' && form.membership_type === 'Vitalicia' && m.membership_type !== 'Vitalicia';
  const canSave = !missing.length && !sponsorMissing && !accessLifetime;
  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const patch = {};
      Object.entries(form).forEach(([k, v]) => { const t = typeof v === 'string' ? v.trim() : v; if ((m[k] || '') !== t) patch[k] = t === '' ? null : t; });
      if (!Object.keys(patch).length) { onClose(); return; }
      await dashboardApi.cdmPatchMember(m.id, patch);
      toast.success('Socio actualizado');
      onSaved();
    } catch (e) { toast.error(e.message || 'No se ha podido guardar'); } finally { setBusy(false); }
  };
  const field = (label, el, req) => (<label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">{label}{req && <span className="text-[#c9a84c]"> *</span>}</span>{el}</label>);
  const opt = (v, l = v) => <option key={v} value={v} className="bg-[#0d1829]">{l}</option>;
  const sel = (k, items, placeholder) => (
    <select value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${inputCls} w-full mt-1`}>
      {(!form[k] || !items.includes(form[k])) && <option value={form[k]} className="bg-[#0d1829]">{form[k] || placeholder}</option>}
      {items.map((v) => opt(v))}
    </select>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" role="dialog" aria-modal="true">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/10 bg-[#0d1829] p-5 sm:p-6 space-y-4">
        <div className="flex justify-between gap-3">
          <div className="min-w-0"><h3 className="text-white font-semibold">{m.id} · {m.name}</h3><p className="text-xs text-gray-400">alta {fdate(m.join_date)}</p></div>
          <button onClick={onClose} aria-label="Cerrar" className="p-1.5 rounded-lg hover:bg-white/[0.05] self-start"><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {field('Origen (ID del broker o del PRO que lo trajo)', <input value={form.sponsor_id} onChange={(e) => setForm({ ...form, sponsor_id: e.target.value.toUpperCase() })} className={`${inputCls} w-full mt-1`} placeholder="B42, EI1…" />, !!m.sponsor_id)}
          {field('Estado', sel('status', lists.member_status || [], 'Elige…'), true)}
          {field('Membresía', sel('sku', lists.sku || ['Access', 'PRO', 'Premium'], 'Elige…'), true)}
          {field('Modalidad', sel('membership_type', lists.membership_type || ['Anual', 'Vitalicia'], 'Elige…'), true)}
          {field('Fecha de alta', <input type="date" value={form.join_date} onChange={(e) => setForm({ ...form, join_date: e.target.value })} className={`${inputCls} w-full mt-1`} />, true)}
          {field('Canal de entrada', (
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className={`${inputCls} w-full mt-1`}>
              {opt('', 'Sin indicar')}
              {(lists.channel || []).map((v) => opt(v))}
            </select>))}
          {field('Email', <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-full mt-1`} />)}
          {field('Teléfono', <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inputCls} w-full mt-1`} />)}
          {field('Tarifa de comisión', (
            <select value={form.rate_override} onChange={(e) => setForm({ ...form, rate_override: e.target.value })} className={`${inputCls} w-full mt-1`}>
              {opt('', 'Según fecha (tabla vigente)')}
              {presets.map((p) => opt(p.key, `${p.label} (${[p.n1, p.n2, p.n3].map((x) => num((x || 0) * 100, 1).replace(/,0$/, '')).join('/')} %)`))}
            </select>))}
        </div>
        {field('Notas', <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} w-full mt-1 resize-none`} />)}
        {m.web_sponsor && <p className="text-xs text-gray-400">Patrocinador según la web: {m.web_sponsor}</p>}
        {(missing.length > 0 || sponsorMissing || accessLifetime) && (
          <p className="text-xs text-red-300" role="alert">
            {accessLifetime ? 'Access solo existe como membresía anual. '
              : `Falta ${[...missing, ...(sponsorMissing ? ['el origen'] : [])].join(', ')}: son campos obligatorios.`}
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <button onClick={onClose} className={`${btnGhost} min-h-[44px] sm:min-h-0`}>Cancelar</button>
          <button onClick={save} disabled={busy || !canSave} className={`${btnGold} min-h-[44px] sm:min-h-0`}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

export default function CdmMiembros({ initialFilter, onChanged, cdm }) {
  const { data, error, reload } = useLoad(() => dashboardApi.cdmMembers(), []);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState(initialFilter || 'todos');
  const [edit, setEdit] = useState(null);
  const rows = useMemo(() => (data?.members || []).filter((m) => {
    if (!(MATCH[filter] || MATCH.todos)(m)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [m.id, m.name, m.email, m.sponsor_id].some((v) => (v || '').toLowerCase().includes(s));
  }), [data, q, filter]);
  if (!data) return <Loading error={error} />;
  const cols = [
    { key: 'id', label: 'ID', className: 'text-white font-medium' },
    { key: 'name', label: 'Socio', render: (m) => (<div><p className="text-white">{m.name}</p><p className="text-xs text-gray-400">{m.email || ''}</p></div>) },
    { key: 'join_date', label: 'Alta', render: (m) => fdate(m.join_date) },
    { key: 'sku', label: 'Plan', render: (m) => (<div className="space-y-1">{m.sku}<div><ModeBadge m={m} /></div></div>) },
    { key: 'sponsor_id', label: 'Origen / broker', render: (m) => (m.missing_broker ? <Badge tone="bad">Sin broker asignado</Badge>
      : m.referrer_id && m.broker_n1 && m.broker_n1 !== m.sponsor_id
        ? (<div>{m.broker_n1}<p className="text-xs text-gray-400">Referido por {m.sponsor_id}{m.sponsor_name ? ` · ${m.sponsor_name}` : ''}</p></div>)
        : (<div>{m.sponsor_id || '—'}<p className="text-xs text-gray-400">{m.sponsor_name || ''}</p></div>)) },
    { key: 'price_eur', label: 'Precio', align: 'right', render: (m) => eur(m.price_eur) },
    { key: 'paid_membership_eur', label: 'Cobrado membresía', align: 'right', render: (m) => eur(m.paid_membership_eur) },
    { key: 'paid_fhct_eur', label: 'Cobrado FHCT', align: 'right', render: (m) => (m.paid_fhct_eur ? eur(m.paid_fhct_eur) : '—') },
    { key: 'status', label: 'Estado', render: (m) => (
      <div className="flex flex-wrap gap-1">
        {m.reserve ? <Badge tone="info">Reserva</Badge> : <Badge tone={m.fully_paid ? 'good' : 'warn'}>{m.fully_paid ? '100 % pagado' : 'Pago incompleto'}</Badge>}
        {m.status && m.status !== 'Abonada' && <Badge>{m.status}</Badge>}
        {m.rate_override && <Badge tone="gold" title={m.rate_label}>Webinar</Badge>}
      </div>) },
    { key: 'tc_signed_at', label: 'T&C', render: (m) => (m.tc_signed_at ? <Badge tone="good">Firmados</Badge> : <Badge>Sin firmar</Badge>) },
    { key: 'fhct_active', label: 'FHCT', align: 'right', render: (m) => (m.fhct_active ? num(m.fhct_active) : '—') },
    { key: 'bricks_member', label: 'Bricks', align: 'right', render: (m) => (m.bricks_member ? num(m.bricks_member, 1) : '—') },
  ];
  return (
    <div className="space-y-4" data-testid="cdm-miembros">
      <div className="flex flex-wrap gap-2 items-center">
        <Search value={q} onChange={setQ} placeholder="Buscar por ID, nombre, email o broker" />
        <div className="flex flex-wrap gap-1">
          {Object.entries(FILTERS).map(([k, v]) => {
            const n = data.members.filter(MATCH[k]).length;
            return <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs border ${filter === k ? 'border-[#c9a84c]/40 text-[#c9a84c] bg-[#c9a84c]/10' : 'border-white/10 text-gray-400'}`}>{v} ({n})</button>;
          })}
        </div>
      </div>
      <p className="text-xs text-gray-400">Pulsa una fila para editarla. Asignar el broker a una venta genera sus comisiones al momento.</p>
      <Table cols={cols} rows={rows} onRow={setEdit} empty="Ningún socio con este filtro" />
      {edit && <MemberEditor m={edit} cdm={cdm} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); onChanged?.(); }} />}
    </div>
  );
}
